import { useQuery, useQueries } from "@tanstack/react-query";
import { AccessControlDoorType, SiteSystemType } from "api/ApiClient";
import axios from "axios";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import { ChevronDown, ChevronUp } from "lucide-react";
import { BASE_API_URL } from "store/Me";
import { GuestType } from "./AdminGuests";
import { formatDateOrDefault } from "helpers";
import type { SiteStatus } from "components/AdminSites/SiteStatusModal";
import type { PropertyStatus } from "components/Properties/hooks/usePropertyData";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible";
import { Switch } from "components/ui/switch";

export default function GuestKeyRings({
  guestId,
  guestTypes,
}: {
  guestId: string;
  guestTypes: GuestType[];
}) {
  const relevantGuestTypes = React.useMemo(
    () =>
      guestTypes.filter(
        (g) => g === GuestType.Staff || g === GuestType.Resident,
      ),
    [guestTypes],
  );

  const [showKeyring, setShowKeyring] = React.useState(false);
  const [isAccessInformationOpen, setIsAccessInformationOpen] =
    React.useState(true);
  const [expanded, setExpanded] = React.useState<
    Record<string, { groups: boolean; locks: boolean }>
  >({});

  const toggleSection = (siteKey: string, section: "groups" | "locks") => {
    setExpanded((prev) => ({
      ...prev,
      [siteKey]: {
        groups: prev[siteKey]?.groups ?? false,
        locks: prev[siteKey]?.locks ?? false,
        [section]: !prev[siteKey]?.[section],
      },
    }));
  };

  const singleGuestType =
    relevantGuestTypes.length === 1 ? relevantGuestTypes[0] : undefined;
  const isMulti = relevantGuestTypes.length === 2;

  // ---- Resolved Access: always fetched, independent of the keyring ----
  const singleResolvedQuery = useGetGuestResolvedAccess(guestId, singleGuestType);
  const multiResolvedQueries = useQueries({
    queries: isMulti
      ? relevantGuestTypes.map((gt) => ({
          queryKey: ["guests", "resolved-access", guestId, gt],
          queryFn: () => getGuestResolvedAccess(guestId, gt),
          enabled: true,
        }))
      : [],
  });
  const resolvedHasError = isMulti
    ? multiResolvedQueries.some((q) => q.isError)
    : singleGuestType
      ? singleResolvedQuery.isError
      : false;
  const resolvedIsLoading = isMulti
    ? multiResolvedQueries.some((q) => q.isLoading)
    : singleGuestType
      ? singleResolvedQuery.isLoading
      : false;

  // ---- Keyring: only fetched once the toggle is switched on ----
  const singleKeyringQuery = useGetGuestKeyRings(guestId, singleGuestType, showKeyring);
  const multiKeyringQueries = useQueries({
    queries: isMulti
      ? relevantGuestTypes.map((gt) => ({
          queryKey: ["guests", "key-rings", guestId, gt],
          queryFn: () => getGuestKeyRings(guestId, gt),
          enabled: showKeyring,
        }))
      : [],
  });
  const keyringHasError = isMulti
    ? multiKeyringQueries.some((q) => q.isError)
    : singleGuestType
      ? singleKeyringQuery.isError
      : false;
  const keyringIsLoading = isMulti
    ? multiKeyringQueries.some((q) => q.isLoading)
    : singleGuestType
      ? singleKeyringQuery.isLoading
      : false;

  if (relevantGuestTypes.length === 0) return <div>No access information</div>;

  const resolvedDatasets: { guestType: GuestType; data: GuestResolvedAccessResponse }[] = [];
  if (isMulti) {
    multiResolvedQueries.forEach((q, idx) => {
      if (q.data)
        resolvedDatasets.push({ guestType: relevantGuestTypes[idx], data: q.data });
    });
  } else if (singleGuestType && singleResolvedQuery.data) {
    resolvedDatasets.push({ guestType: singleGuestType, data: singleResolvedQuery.data });
  }

  const keyringDatasets: { guestType: GuestType; data: GuestKeyRingResponse }[] = [];
  if (isMulti) {
    multiKeyringQueries.forEach((q, idx) => {
      if (q.data)
        keyringDatasets.push({ guestType: relevantGuestTypes[idx], data: q.data });
    });
  } else if (singleGuestType && singleKeyringQuery.data) {
    keyringDatasets.push({ guestType: singleGuestType, data: singleKeyringQuery.data });
  }

  return (
    <div>
      <Collapsible open={isAccessInformationOpen} onOpenChange={setIsAccessInformationOpen}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-lg font-semibold">
            <CollapsibleTrigger asChild>
              <button
                aria-label="Toggle access information"
                className="flex items-center gap-1 text-left"
                type="button"
              >
                Access Information
                {isAccessInformationOpen ? (
                  <ChevronUp aria-hidden="true" className="size-4 text-slate-500" />
                ) : (
                  <ChevronDown aria-hidden="true" className="size-4 text-slate-500" />
                )}
              </button>
            </CollapsibleTrigger>
          </h4>
          <div className="flex items-center gap-2">
            <Switch
              id="guest-key-rings-toggle"
              checked={showKeyring}
              onCheckedChange={setShowKeyring}
              size="sm"
            />
            <label className="text-sm text-slate-600" htmlFor="guest-key-rings-toggle">
              Keyring
            </label>
          </div>
        </div>
        <CollapsibleContent>
          {!showKeyring && (
            <div className="mt-4 space-y-8">
              {resolvedHasError && <div>There was an error</div>}
              {resolvedIsLoading && <div>loading...</div>}
              {!resolvedIsLoading &&
                !resolvedHasError &&
                resolvedDatasets.map(({ guestType: dsGuestType, data }) => (
                  <div key={dsGuestType} className="space-y-6">
                    {isMulti && (
                      <h5 className="mb-2 text-sm font-semibold tracking-wide uppercase text-slate-500">
                        {GuestType[dsGuestType] || dsGuestType}
                      </h5>
                    )}
                    {data.sites.length === 0 && (
                      <p className="text-sm italic text-slate-500">No resolved access</p>
                    )}
                    {data.sites.map((site) => {
                      const siteKey = `resolved-${dsGuestType}-${site.id}`;
                      return (
                        <SiteAccessCard
                          key={siteKey}
                          site={site}
                          isGroupsOpen={!!expanded[siteKey]?.groups}
                          isLocksOpen={!!expanded[siteKey]?.locks}
                          onToggleGroups={() => toggleSection(siteKey, "groups")}
                          onToggleLocks={() => toggleSection(siteKey, "locks")}
                        />
                      );
                    })}
                  </div>
                ))}
            </div>
          )}
          {showKeyring && (
            <div className="mt-4 space-y-8">
              {keyringHasError && <div>There was an error</div>}
              {keyringIsLoading && <div>loading...</div>}
              {!keyringIsLoading &&
                !keyringHasError &&
                keyringDatasets.map(({ guestType: dsGuestType, data }) => (
                  <div key={dsGuestType} className="space-y-6">
                    {isMulti && (
                      <h5 className="mb-2 text-sm font-semibold tracking-wide uppercase text-slate-500">
                        {GuestType[dsGuestType] || dsGuestType}
                      </h5>
                    )}
                    {data.sites.map((site) => {
                      const siteKey = `keyring-${dsGuestType}-${site.id}`;
                      return (
                        <SiteAccessCard
                          key={siteKey}
                          site={site}
                          isGroupsOpen={!!expanded[siteKey]?.groups}
                          isLocksOpen={!!expanded[siteKey]?.locks}
                          onToggleGroups={() => toggleSection(siteKey, "groups")}
                          onToggleLocks={() => toggleSection(siteKey, "locks")}
                        />
                      );
                    })}
                  </div>
                ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ---- SHARED SITE CARD UI ----

const badgeBase =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const doorTypeColor = (doorType: AccessControlDoorType) => {
  switch (doorType) {
    case AccessControlDoorType.Guest:
      return "bg-amber-100 text-amber-700 ring-1 ring-amber-200";
    case AccessControlDoorType.Common:
      return "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200";
    case AccessControlDoorType.Door:
      return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200";
    default:
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200";
  }
};

interface SiteAccessCardSite {
  id: string;
  name: string;
  systemType: SiteSystemType;
  status?: SiteStatus;
  propertyStatuses?: {
    propertyExternalId: string | null;
    name: string | null;
    status?: PropertyStatus | null;
  }[];
  groups: {
    id: string;
    name: string;
    validFrom: string | null;
    validTo: string | null;
  }[];
  locks: {
    id: string;
    name: string;
    systemLockId: string;
    validFrom: string | null;
    validTo: string | null;
    doorType: AccessControlDoorType;
  }[];
}

function SiteAccessCard({
  site,
  isGroupsOpen,
  isLocksOpen,
  onToggleGroups,
  onToggleLocks,
}: {
  site: SiteAccessCardSite;
  isGroupsOpen: boolean;
  isLocksOpen: boolean;
  onToggleGroups: () => void;
  onToggleLocks: () => void;
}) {
  return (
    <div className="p-4 space-y-4 border rounded-lg shadow-sm border-slate-200 bg-slate-50">
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-base text-slate-800">{site.name}</strong>
        <span className={`${badgeBase} bg-slate-200 text-slate-700 ring-1 ring-inset ring-black/5`}>
          {site.systemType}
        </span>
        {site.status && (
          <Badge variant={statusBadgeVariant(site.status)}>{site.status}</Badge>
        )}
        {site.propertyStatuses?.map((p, idx) => (
          <Badge
            key={p.propertyExternalId ?? p.name ?? idx}
            variant={statusBadgeVariant(p.status)}
          >
            {p.name ?? p.propertyExternalId}: {p.status ?? "Staging"}
          </Badge>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h6
            className="text-xs font-semibold tracking-wide uppercase cursor-pointer text-slate-500"
            onClick={onToggleGroups}
          >
            Groups ({site.groups.length})
          </h6>
          {site.groups.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onToggleGroups}
              type="button"
            >
              {isGroupsOpen ? <ChevronUp /> : <ChevronDown />}
              {isGroupsOpen ? "Hide" : "Show"}
            </Button>
          )}
        </div>
        {site.groups.length === 0 && (
          <p className="text-xs italic text-slate-500">No groups</p>
        )}
        {isGroupsOpen && (
          <div className="grid gap-3 md:grid-cols-4">
            {site.groups.map((group) => (
              <div
                key={group.id}
                className="flex flex-col gap-1 p-3 bg-white border rounded-md shadow-sm border-slate-200"
              >
                <div className="text-sm font-medium text-slate-800">{group.name}</div>
                <div className="text-[10px] text-slate-500 break-all">{group.id}</div>
                <div className="text-[11px] text-slate-600">
                  Valid: {formatDateOrDefault(group.validFrom, "lll")} →{" "}
                  {formatDateOrDefault(group.validTo, "lll")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h6
            className="text-xs font-semibold tracking-wide uppercase cursor-pointer text-slate-500"
            onClick={onToggleLocks}
          >
            Locks ({site.locks.length})
          </h6>
          {site.locks.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onToggleLocks}
              type="button"
            >
              {isLocksOpen ? <ChevronUp /> : <ChevronDown />}
              {isLocksOpen ? "Hide" : "Show"}
            </Button>
          )}
        </div>
        {site.locks.length === 0 && (
          <p className="text-xs italic text-slate-500">No locks</p>
        )}
        {isLocksOpen && (
          <div className="grid gap-3 md:grid-cols-4">
            {site.locks.map((lock) => (
              <div
                key={lock.id}
                className="flex flex-col gap-1 p-3 bg-white border rounded-md shadow-sm border-slate-200"
              >
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faKey} className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{lock.name}</span>
                  <span className={`${badgeBase} ${doorTypeColor(lock.doorType)}`}>
                    {lock.doorType}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 break-all">{lock.systemLockId}</div>
                <div className="text-[11px] text-slate-600">
                  Valid: {formatDateOrDefault(lock.validFrom, "lll")} →{" "}
                  {formatDateOrDefault(lock.validTo, "lll")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- TYPES & DATA FETCH ----

interface GuestKeyRingResponseSiteGroup {
  id: string;
  name: string;
  validFrom: string | null;
  validTo: string | null;
}
interface GuestKeyRingResponseSiteLock {
  id: string;
  name: string;
  systemLockId: string;
  validFrom: string | null;
  validTo: string | null;
  doorType: AccessControlDoorType;
  hasCurrentAccess: boolean;
}
interface GuestKeyRingResponseSite {
  id: string;
  name: string;
  systemType: SiteSystemType;
  groups: GuestKeyRingResponseSiteGroup[];
  locks: GuestKeyRingResponseSiteLock[];
  validFrom: string | null;
  validTo: string | null;
}
interface GuestKeyRingResponse {
  validFrom: string | null;
  validTo: string | null;
  sites: GuestKeyRingResponseSite[];
}

async function getGuestKeyRings(guestId: string, guestType: GuestType) {
  const url = `${BASE_API_URL}/api/guest/${guestId}/key-ring?guestType=${guestType}`;
  const response = await axios.get<GuestKeyRingResponse>(url);

  return response.data;
}

function useGetGuestKeyRings(guestId: string, guestType?: GuestType, enabled = true) {
  return useQuery({
    queryKey: ["guests", "key-rings", guestId, guestType],
    queryFn: () => getGuestKeyRings(guestId, guestType!),
    enabled: !!guestType && enabled,
  });
}

// ---- Resolved Access: independent of the keyring (no invite requirement, no system-type
// filter, no active-only exclusion). Resolved directly from the guest's access control groups. ----

interface GuestResolvedAccessGroupResponse {
  id: string;
  name: string;
  validFrom: string | null;
  validTo: string | null;
}
interface GuestResolvedAccessLockResponse {
  id: string;
  name: string;
  systemLockId: string;
  doorType: AccessControlDoorType;
  validFrom: string | null;
  validTo: string | null;
}
interface GuestResolvedAccessPropertyStatus {
  propertyExternalId: string | null;
  name: string | null;
  status?: PropertyStatus | null;
}
interface GuestResolvedAccessSiteResponse {
  id: string;
  name: string;
  systemType: SiteSystemType;
  status: SiteStatus;
  propertyStatuses: GuestResolvedAccessPropertyStatus[];
  groups: GuestResolvedAccessGroupResponse[];
  locks: GuestResolvedAccessLockResponse[];
  validFrom: string | null;
  validTo: string | null;
}
interface GuestResolvedAccessResponse {
  validFrom: string | null;
  validTo: string | null;
  sites: GuestResolvedAccessSiteResponse[];
}

async function getGuestResolvedAccess(guestId: string, guestType: GuestType) {
  const url = `${BASE_API_URL}/api/guest/${guestId}/resolved-access?guestType=${guestType}`;
  const response = await axios.get<GuestResolvedAccessResponse>(url);

  return response.data;
}

function useGetGuestResolvedAccess(guestId: string, guestType?: GuestType) {
  return useQuery({
    queryKey: ["guests", "resolved-access", guestId, guestType],
    queryFn: () => getGuestResolvedAccess(guestId, guestType!),
    enabled: !!guestType,
  });
}

